// The live half of the source switch on Review, Performance.
//
// Follows `leafGridApi.ts` rather than the store pattern: one page-scoped read
// with no shared state, and `src/store/index.ts` is edited on every other
// branch in flight.
import axios from 'axios';

import { FunnelSegment } from './PerformanceBlocks';

export interface LivePerformance {
  /** Which slice of the Predictions data this is. */
  audience: string;
  conversionType: string;
  /** The periods the sums cover, oldest first. */
  periods: string[];
  funnel: Record<string, FunnelSegment>;
  /** Per segment, already in percent. */
  rates: Record<string, number>;
  audiences: string[];
  conversionTypes: string[];
  /**
   * The slice that was asked for and is not the one above.
   *
   * The server draws each model's live numbers on the audience and conversion
   * goal that model was TRAINED on. When production holds nothing for that
   * audience it falls back and says which one it wanted, so the page can name
   * the gap rather than present a different population as this model's result.
   * Absent on an ordinary reading.
   */
  requestedAudience?: string;
  requestedConversionType?: string;
  /**
   * Whether the server knew which slice this model trained on at all.
   *
   * Absent is NOT "it matched": a model whose training params were never read
   * is also absent here, and both used to render as "same slice this model was
   * trained on", asserting an agreement nothing had checked.
   */
  trainedOnKnown?: boolean;
}

/**
 * The model as running, or null when there is nothing to show.
 *
 * Null covers not deployed, deployed but not processed yet, and bongo
 * unreachable, because the page does the same thing in all three: keeps the
 * validation source and says the live one is not answering. Distinguishing
 * them would put a reason on screen that only the server can tell apart, and
 * the reason does not change what the reader can do.
 */
export function fetchLivePerformance(
  tenant: string | number,
  modelId: string | number,
  segments: string[]
): Promise<LivePerformance | null> {
  return axios
    .get<LivePerformance | null>(
      `/api/tenant/${tenant}/model/${modelId}/performance/live`,
      { params: { segments: segments.join(',') } }
    )
    .then((response) => response.data ?? null)
    .catch(() => null);
}
